import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Button } from "@/shared/ui/core";
import { cn } from "@/shared/lib/cn";
import { CtaHeading } from "./CtaHeading";
import { CtaPill } from "./CtaPill";
import { CtaSubtitle } from "./CtaSubtitle";

export interface CtaBannerProps {
  /** Texto de la píldora superior. */
  eyebrow: string;
  /** Icono de la píldora. */
  eyebrowIcon?: ReactNode;
  /** Título completo, ya traducido. */
  title: string;
  /** La parte del título que va resaltada. */
  highlight?: string;
  subtitle?: string;
  actionLabel: string;
  /** Ruta interna del botón. */
  actionTo: string;
  /** Icono a la izquierda del botón. */
  actionIcon?: ReactNode;
  className?: string;
}

/**
 * El bloque oscuro de cierre — "¿Listo para transformar tu bienestar?".
 *
 * ── Todo entra por props, nada se escribe dentro ─────────────────────
 * Este bloque aparece al final de la tienda con un mensaje, y va a
 * aparecer en otras pantallas con otro. Si el texto viviera dentro, la
 * segunda pantalla lo copiaría con un `if` — y a la tercera esto sería un
 * componente con tres modos. Recibiendo el contenido, el componente solo
 * sabe de la FORMA: fondo en tinta, píldora arriba, título con una
 * palabra resaltada, subtítulo y un botón.
 *
 * Un CTA de cierre es un solo camino: hay un botón, no una lista de
 * opciones. Ofrecer dos es lo que hace que no se pulse ninguno.
 *
 * ══════════════════════════════════════════════════════════════════════
 * EL FONDO ES PLANO, Y NO ES SOLO ESTÉTICA
 *
 * Era `bg-ds-grad-ink` —#1F1D2B → #2E2540 → #3A2F55 → #6B5B95— más un
 * halo violeta difuminado en la esquina. Anahi lo retiró el 26-ago («hay
 * que evitar estos bg con degradado»), y al medirlo resultó que además
 * NO PASABA por el extremo claro. Contra el último tramo del degradado,
 * #6B5B95:
 *
 *   blanco (el título)          5.9 : 1   ✓
 *   ds-lilac (el resalte)       3.4 : 1   ✗
 *   white/70 (el subtítulo)     3.8 : 1   ✗
 *   white/80 (la píldora)       4.5 : 1   ✗ (justo en el límite)
 *
 * Ese es el problema de medir un degradado: el número cambia según por
 * dónde caiga la letra, y el "peor punto" depende del ancho de la
 * ventana. Con el fondo plano en `ds-ink` (#1F1D2B) hay UNA cifra por
 * color, y las cuatro pasan de sobra:
 *
 *   blanco                     16.6 : 1
 *   ds-lilac                    9.4 : 1
 *   white/70                    8.7 : 1
 *   white/80                   11.0 : 1
 *
 * El mínimo de la WCAG para texto pequeño es 4.5 : 1.
 *
 * El halo violeta de la esquina se fue con el degradado: era un
 * `radial-gradient` decorativo y, sobre todo, era lo que seguía haciendo
 * que la tarjeta se leyera "violeta arriba, oscura abajo" aunque el
 * degradado de base ya no estuviera. Lo que separa este bloque de la
 * página es el color plano y el radio, no una mancha de luz.
 * ══════════════════════════════════════════════════════════════════════
 */
export function CtaBanner({
  eyebrow,
  eyebrowIcon,
  title,
  highlight,
  subtitle,
  actionLabel,
  actionTo,
  actionIcon,
  className,
}: CtaBannerProps) {
  return (
    <section className={cn("mx-auto w-full max-w-4xl px-6 sm:px-8", className)}>
      <div className="relative overflow-hidden rounded-ds-xl bg-ds-ink px-6 py-12 text-center sm:px-14 sm:py-14">
        <div className="relative">
          <CtaPill icon={eyebrowIcon}>{eyebrow}</CtaPill>

          <CtaHeading highlight={highlight}>{title}</CtaHeading>

          {subtitle && <CtaSubtitle>{subtitle}</CtaSubtitle>}

          <Button
            as={Link}
            to={actionTo}
            variant="inverse"
            size="lg"
            leftIcon={actionIcon}
            rightIcon={<ArrowRight />}
            className="mt-8"
          >
            {actionLabel}
          </Button>
        </div>
      </div>
    </section>
  );
}
